import React, { createContext, useContext, ReactNode, useMemo, useCallback, useEffect } from 'react';
import { Firma } from '../types'; 
import { useAuth } from './AuthContext';
import { useAuthStateManager } from '../hooks/useAuthStateManager';
import { usePersistedState } from '../hooks/usePersistedState';

interface AppContextType {
  selectedFirma: Firma | null;
  setSelectedFirma: (firma: Firma | null) => void;
  selectedYil: number;
  setSelectedYil: (yil: number) => void;
  isReady: boolean;
  canProceed: boolean;
  authState: string;
  authError: string | null;
  retry: () => void;
  renderGuard: () => React.ReactElement | null;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProviderV2: React.FC<{ children: ReactNode }> = React.memo(({ children }) => {
  const { user, profile } = useAuth();
  const { state: authState, isReady: authReady, error: authError, retry, canProceed: authCanProceed } = useAuthStateManager();
  
  const [selectedFirma, setSelectedFirmaState] = usePersistedState<Firma | null>('selectedFirma', null);
  const [selectedYil, setSelectedYil] = usePersistedState<number>('selectedYil', new Date().getFullYear());
  
  // Firma değişiminde cache temizleme ile birlikte setter
  const setSelectedFirma = useCallback((firma: Firma | null) => {
    // Önceki firma ile karşılaştır
    const previousFirmaId = selectedFirma?.id;
    const newFirmaId = firma?.id;
    
    if (previousFirmaId !== newFirmaId) {
      // Modül cache'lerini temizle
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('moduleState_') || 
            key.startsWith('veriToplamaCache_') ||
            key.startsWith('hedeflerCache_') ||
            key.startsWith('risklerCache_')) {
          localStorage.removeItem(key);
        }
      });
      
      // Session storage'daki geçici verileri de temizle
      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('firma') || key.includes('konu')) {
          sessionStorage.removeItem(key);
        }
      });
    }
    
    setSelectedFirmaState(firma);
  }, [selectedFirma?.id, setSelectedFirmaState]);

  // Temp session için otomatik firma seçimi
  useEffect(() => {
    if (authCanProceed && !selectedFirma) {
      const tempSession = localStorage.getItem('temp_user_session');
      if (tempSession) {
        try {
          const sessionData = JSON.parse(tempSession);
          if (sessionData.userFirmas && sessionData.userFirmas.length > 0) {
            const firstFirma = sessionData.userFirmas[0];
            const firma: Firma = {
              id: firstFirma.firma_id,
              value: firstFirma.firma_adi,
              label: firstFirma.firma_adi,
              firma_adi: firstFirma.firma_adi
            };
            setSelectedFirma(firma);
          }
        } catch (error) {
          // Silent fail
        }
      }
    }
  }, [authCanProceed, selectedFirma, setSelectedFirma]);

  // Basit mantık - sadece auth durumu ve seçimler
  const canProceed = Boolean(authCanProceed && selectedFirma && selectedYil);
  const requiresSelection = authCanProceed && (!selectedFirma || !selectedYil);

  const renderGuard = useCallback(() => {
    if (!authReady) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Sistem hazırlanıyor...</p>
            {authError && (
              <div className="mt-4">
                <p className="text-red-600 text-sm mb-2">{authError}</p>
                <button onClick={retry} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  Tekrar Dene
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (requiresSelection) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="max-w-md mx-auto text-center p-6 bg-white rounded-lg shadow-lg">
            <div className="h-16 w-16 text-amber-500 mx-auto mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Firma ve Yıl Seçimi Gerekli</h2>
            <p className="text-gray-600 mb-4">Devam etmek için lütfen üst menüden firma ve raporlama yılı seçin.</p>
          </div>
        </div>
      );
    }

    return null;
  }, [authReady, requiresSelection, authError, retry]);

  const value = useMemo(() => ({
    selectedFirma,
    setSelectedFirma,
    selectedYil,
    setSelectedYil,
    isReady: authReady,
    canProceed,
    authState,
    authError,
    retry,
    renderGuard
  }), [selectedFirma, selectedYil, authReady, canProceed, authState, authError, retry, renderGuard]);
  
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
});

export const useAppContextV2 = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) throw new Error('useAppContextV2 must be used within an AppProviderV2');
  return context;
};
